import RouteTemplate from 'ember-route-template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { gt } from 'ember-truth-helpers';
import { fn } from '@ember/helper';
import ResultComponent from '../components/search/result';
import SocialComponent from '../components/widget/sidebar/social';
import PopularPostsComponent from '../components/widget/sidebar/popular-posts';
import TagCloudComponent from '../components/widget/sidebar/tag-cloud';
import LiveNewsTicker from '../components/layout/live-news-ticker';
import PaginationComponent from '../components/layout/pagination';

import type { SearchResult } from '../types/search';
import type RouterService from '@ember/routing/router-service';
import type SearchService from '../services/search';

interface SearchModel {
  searchResponse: any;
  hasQuery: boolean;
  query: string;
  error?: string;
  recentSearches: string[];
  searchResults: SearchResult[];
  totalResults: number;
  currentPage: number;
  totalPages: number;
  hasResults: boolean;
  hasError: boolean;
  errorMessage?: string;
  searchQuery: string;
  searchSuggestions: string[];
}

class SearchRouteComponent extends Component<{ model: SearchModel }> {
  @service declare router: RouterService;
  @service declare search: SearchService;

  get hasQuery() {
    return this.args.model.hasQuery && this.args.model.searchQuery;
  }

  get hasResults() {
    return this.hasQuery && this.args.model.hasResults;
  }

  get hasError() {
    return this.hasQuery && this.args.model.hasError;
  }

  get noResults() {
    return this.hasQuery && !this.args.model.hasResults && !this.args.model.hasError;
  }

  get showRecentSearches() {
    return !this.hasQuery && this.args.model.recentSearches?.length > 0;
  }

  get resultText() {
    const total = this.args.model.totalResults || 0;
    const query = this.args.model.searchQuery || '';

    if (total === 0) {
      return `No results found for: "${query}"`;
    } else if (total === 1) {
      return `result found for: "${query}"`;
    } else {
      return `results found for: "${query}"`;
    }
  }

  @action
  handleResultClick(result: SearchResult, position: number) {
    // Track the click via the route action
    this.router.send('trackResultClick', result, position);
  }

  @action
  handlePageChange(page: number) {
    this.router.send('goToPage', page);
  }

  @action
  performRecentSearch(query: string) {
    this.router.transitionTo('search', { queryParams: { q: query } } as any);
  }

  <template>
    <div class='banner-wrap forum-banner'>
      <div class='banner grid-limit'>
        <h2 class='banner-title'>
          {{#if this.hasQuery}}
            Search Results
          {{else}}
            Search
          {{/if}}
        </h2>
        <p class='banner-sections'>
          <span class='banner-section'>Search</span>
          <svg class='arrow-icon'>
            <use href='#svg-arrow'></use>
          </svg>
          <span class='banner-section'>1vX Gaming</span>
        </p>
      </div>
    </div>
    <LiveNewsTicker />
    <div class='layout-content-1 layout-item-3-1 grid-limit'>
      <div class='layout-body'>

        {{#if this.hasQuery}}
          {{! Search Results Section }}
          <div class='section-title-wrap blue'>
            <h2 class='section-title medium'>Search Results</h2>
            <div class='section-title-separator'></div>
          </div>

          <div class='filter-heading'>
            <p class='filter-heading-text'>
              {{#if this.hasResults}}
                <span class='highlight'>{{@model.totalResults}}</span>
                {{{this.resultText}}}
              {{else if this.hasError}}
                <span class='error'>Error: {{@model.errorMessage}}</span>
              {{else}}
                {{{this.resultText}}}
              {{/if}}
            </p>
          </div>

          {{#if this.hasResults}}
            <div class='post-preview-showcase grid3-1col centered'>
              {{#each @model.searchResults as |result index|}}
                <ResultComponent
                  @result={{result}}
                  @position={{index}}
                  @query={{@model.searchQuery}}
                  @onResultClick={{this.handleResultClick}}
                />
              {{/each}}
            </div>

            {{#if (gt @model.totalPages 1)}}
              <PaginationComponent
                @currentPage={{@model.currentPage}}
                @totalPages={{@model.totalPages}}
                @onPageChange={{this.handlePageChange}}
              />
            {{/if}}

          {{else if this.noResults}}
            {{! No Results State }}
            <div class='search-no-results'>
              <div class='no-results-icon'>
                <svg class='search-icon large'>
                  <use href='#svg-search'></use>
                </svg>
              </div>
              <h3>No results found</h3>
              <p>Try adjusting your search terms or check the spelling.</p>

              {{#if @model.searchSuggestions.length}}
                <div class='search-suggestions'>
                  <p><strong>Did you mean:</strong></p>
                  <ul class='suggestion-list'>
                    {{#each @model.searchSuggestions as |suggestion|}}
                      <li>
                        <button
                          type='button'
                          class='suggestion-link'
                          {{on 'click' (fn this.performRecentSearch suggestion)}}
                        >
                          {{suggestion}}
                        </button>
                      </li>
                    {{/each}}
                  </ul>
                </div>
              {{/if}}
            </div>

          {{else if this.hasError}}
            {{! Error State }}
            <div class='search-error'>
              <div class='error-icon'>
                <svg class='warning-icon large'>
                  <use href='#svg-warning'></use>
                </svg>
              </div>
              <h3>Search Error</h3>
              <p>{{@model.errorMessage}}</p>
              <p>Please try again or contact support if the problem persists.</p>
            </div>
          {{/if}}

        {{else}}
          {{! Empty Search State }}
          <div class='section-title-wrap blue'>
            <h2 class='section-title medium'>Search 1vX.gg</h2>
            <div class='section-title-separator'></div>
          </div>

          <div class='search-empty-state'>
            <div class='search-placeholder'>
              <svg class='search-icon large'>
                <use href='#svg-search'></use>
              </svg>
              <h3>What are you looking for?</h3>
              <p>Search for gaming news, esports coverage, team profiles, player stats, and tournament information.</p>
            </div>

            {{#if this.showRecentSearches}}
              <div class='recent-searches'>
                <h4>Recent Searches</h4>
                <ul class='recent-search-list'>
                  {{#each @model.recentSearches as |query|}}
                    <li>
                      <button
                        type='button'
                        class='recent-search-link'
                        {{on 'click' (fn this.performRecentSearch query)}}
                      >
                        <svg class='search-icon small'>
                          <use href='#svg-search'></use>
                        </svg>
                        {{query}}
                      </button>
                    </li>
                  {{/each}}
                </ul>
              </div>
            {{/if}}

            <div class='search-tips'>
              <h4>Search Tips</h4>
              <ul class='tips-list'>
                <li><strong>Teams:</strong> Search for "Team Phoenix" or "TSM"</li>
                <li><strong>Players:</strong> Search for player names like "ShadowStrike"</li>
                <li><strong>Games:</strong> Search for "SMITE", "League of Legends", or "Valorant"</li>
                <li><strong>News:</strong> Search for "tournament", "championship", or "results"</li>
              </ul>
            </div>
          </div>
        {{/if}}

      </div>

      <div class='layout-sidebar layout-item gutter-medium'>
        <SocialComponent />
        <PopularPostsComponent />
        <div class='widget-sidebar'>
          <div class='promo-advert'>
            <a href='#'>
              <img class='promo-advert-img' src='/img/banners/promo-ad-1.jpg' alt='promo-ad-1' />
            </a>
          </div>
        </div>
        <TagCloudComponent />
      </div>
    </div>
  </template>
}
export default RouteTemplate(SearchRouteComponent);
